import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Image as ImageIcon,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Gauge,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STIL_CRITERIA = [
  "kein Text im Bild",
  "keine Gesichter",
  "einheitlicher Stil",
  "passt zur Aussage",
  "keine Zusatzinfos",
];

const TILE_LABEL = {
  gefahrenmarkierung: "Gefahrenmarkierung",
  richtig_falsch_falsch: "Richtig/Falsch · Falsch",
  richtig_falsch_richtig: "Richtig/Falsch · Maßnahme",
};

function fmtSec(v) {
  if (v === null || v === undefined) return "–";
  return `${Number(v).toFixed(2)} s`;
}

function StatPill({ label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-slate-200 bg-white px-4 py-3"
    >
      <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-1 text-lg font-semibold text-slate-800">{value}</div>
    </div>
  );
}

function PromptDetails({ rec }) {
  return (
    <details className="text-xs text-slate-500">
      <summary className="cursor-pointer select-none font-medium text-slate-600">
        Bildprompt anzeigen
      </summary>
      <p
        data-testid={`preview-image-prompt-${rec.index}`}
        className="mt-2 whitespace-pre-wrap break-words rounded-lg bg-slate-50 p-2 font-mono text-[11px] leading-relaxed text-slate-600"
      >
        {rec.prompt}
      </p>
      {rec.prompt_sha256 && (
        <p className="mt-1 break-all text-[10px] text-slate-400">
          SHA256: {rec.prompt_sha256}
        </p>
      )}
    </details>
  );
}

function Timings({ rec }) {
  return (
    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-slate-500">
      <span>Prompt: {fmtSec(rec.prompt_build_seconds)}</span>
      <span>KI: {fmtSec(rec.ai_seconds)}</span>
      <span>Speichern: {fmtSec(rec.save_seconds)}</span>
      <span
        data-testid={`preview-image-total-${rec.index}`}
        className="font-semibold text-slate-700"
      >
        Gesamt: {fmtSec(rec.total_seconds)}
      </span>
    </div>
  );
}

function Frame({ rec, src, label }) {
  const ok = rec.status === "success";
  const isError = rec.status === "error";
  return (
    <div className="flex flex-1 flex-col">
      <span className="mb-1 text-[11px] font-medium uppercase tracking-wide text-slate-400">
        {label}
      </span>
      <div className="relative flex aspect-square items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
        {src ? (
          <img src={src} alt={rec.source_text} className="h-full w-full object-contain" />
        ) : isError ? (
          <div className="flex flex-col items-center gap-2 p-4 text-center text-rose-600">
            <AlertTriangle className="h-7 w-7" />
            <span className="text-[11px]">{rec.error || "Fehler"}</span>
          </div>
        ) : ok ? (
          <span className="p-4 text-center text-[11px] text-slate-400">
            kein Bild vorhanden
          </span>
        ) : (
          <Loader2 className="h-7 w-7 animate-spin text-slate-300" />
        )}
      </div>
    </div>
  );
}

function ImageCard({ rec }) {
  const ok = rec.status === "success";
  return (
    <div
      data-testid={`preview-image-card-${rec.index}`}
      className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
    >
      <div className="relative flex aspect-square items-center justify-center bg-slate-50">
        {ok && rec.url ? (
          <img
            data-testid={`preview-image-img-${rec.index}`}
            src={`${BACKEND_URL}${rec.url}`}
            alt={rec.source_text}
            className="h-full w-full object-contain"
          />
        ) : rec.status === "error" ? (
          <div className="flex flex-col items-center gap-2 p-6 text-center text-rose-600">
            <AlertTriangle className="h-8 w-8" />
            <span className="text-xs">{rec.error || "Fehler"}</span>
          </div>
        ) : (
          <Loader2 className="h-8 w-8 animate-spin text-slate-300" />
        )}
        <span
          className={`absolute left-3 top-3 rounded-full px-2 py-0.5 text-[11px] font-medium ${
            rec.kind === "hazard"
              ? "bg-amber-100 text-amber-800"
              : "bg-emerald-100 text-emerald-800"
          }`}
        >
          {rec.kind === "hazard" ? "Gefahr" : "Maßnahme / PSA"}
        </span>
        {ok && <CheckCircle2 className="absolute right-3 top-3 h-5 w-5 text-emerald-500" />}
      </div>
      <div className="flex flex-1 flex-col gap-3 p-4">
        <p
          data-testid={`preview-image-source-${rec.index}`}
          className="text-sm font-medium leading-snug text-slate-800"
        >
          {rec.source_text}
        </p>
        {rec.tile && (
          <span className="w-fit rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">
            {TILE_LABEL[rec.tile] || rec.tile}
          </span>
        )}
        <PromptDetails rec={rec} />
        <div className="mt-auto">
          <Timings rec={rec} />
        </div>
        <div className="text-[11px] text-slate-400">
          {rec.width && rec.height ? `${rec.width}×${rec.height} px · ` : ""}
          {rec.size_bytes ? `${Math.round(rec.size_bytes / 1024)} KB · ` : ""}
          {rec.model}
        </div>
      </div>
    </div>
  );
}

function StilCard({ rec, oldLabel = "Alt · IMPLEMENT-11", newLabel = "Neu · CLartext-Stil" }) {
  return (
    <div
      data-testid={`stil-image-card-${rec.index}`}
      className="flex flex-col gap-3 overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
    >
      <div className="flex items-center justify-between">
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
            rec.kind === "hazard"
              ? "bg-amber-100 text-amber-800"
              : "bg-emerald-100 text-emerald-800"
          }`}
        >
          {rec.kind === "hazard" ? "Gefahr" : "Maßnahme / PSA"}
        </span>
        {rec.status === "success" && (
          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
        )}
      </div>

      <p
        data-testid={`stil-image-source-${rec.index}`}
        className="text-sm font-medium leading-snug text-slate-800"
      >
        {rec.source_text}
      </p>

      <div className="flex gap-3">
        <Frame
          rec={rec}
          label={oldLabel}
          src={rec.old_url ? `${BACKEND_URL}${rec.old_url}` : null}
        />
        <Frame
          rec={rec}
          label={newLabel}
          src={
            rec.status === "success" && rec.url ? `${BACKEND_URL}${rec.url}` : null
          }
        />
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid={`stil-criteria-${rec.index}`}>
        {STIL_CRITERIA.map((c) => (
          <span
            key={c}
            className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] text-slate-500"
          >
            {c}?
          </span>
        ))}
      </div>

      <PromptDetails rec={rec} />
      <Timings rec={rec} />
      <div className="text-[11px] text-slate-400">
        {rec.width && rec.height ? `${rec.width}×${rec.height} px · ` : ""}
        {rec.size_bytes ? `${Math.round(rec.size_bytes / 1024)} KB · ` : ""}
        {rec.model}
      </div>
    </div>
  );
}

export default function PreviewImages() {
  const [mode, setMode] = useState("basis"); // "basis" | "stil" | "anker" | "welt"
  const [basisJob, setBasisJob] = useState(null);
  const [stilJob, setStilJob] = useState(null);
  const [ankerJob, setAnkerJob] = useState(null);
  const [weltJob, setWeltJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const job =
    mode === "basis" ? basisJob : mode === "stil" ? stilJob : mode === "anker" ? ankerJob : weltJob;
  const setJob =
    mode === "basis"
      ? setBasisJob
      : mode === "stil"
      ? setStilJob
      : mode === "anker"
      ? setAnkerJob
      : setWeltJob;

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const poll = useCallback(
    async (jobId, setter) => {
      try {
        const data = await api.getPreviewImagesStatus(jobId);
        setter(data);
        if (data.status !== "running") stopPolling();
      } catch (e) {
        stopPolling();
        setError("Status konnte nicht geladen werden.");
      }
    },
    [stopPolling]
  );

  // Beim Laden: jüngste Läufe beider Modi wiederherstellen.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.getLatestPreviewImages().catch(() => ({ job: null })),
      api.getLatestStilImages().catch(() => ({ job: null })),
      api.getLatestAnchorImages().catch(() => ({ job: null })),
      api.getLatestWeltImages().catch(() => ({ job: null })),
    ]).then(([basis, stil, anker, welt]) => {
      if (cancelled) return;
      if (basis?.job) setBasisJob(basis.job);
      if (stil?.job) setStilJob(stil.job);
      if (anker?.job) setAnkerJob(anker.job);
      if (welt?.job) setWeltJob(welt.job);
      const setters = {
        impl11: setBasisJob,
        stil: setStilJob,
        anker: setAnkerJob,
        welt: setWeltJob,
      };
      const running = [basis?.job, stil?.job, anker?.job, welt?.job].find(
        (j) => j?.status === "running"
      );
      if (running) {
        const m =
          running.mode === "stil"
            ? "stil"
            : running.mode === "anker"
            ? "anker"
            : running.mode === "welt"
            ? "welt"
            : "basis";
        setMode(m);
        pollRef.current = setInterval(
          () => poll(running.job_id, setters[running.mode] || setBasisJob),
          2000
        );
      }
    });
    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [poll, stopPolling]);

  const handleStart = async () => {
    setError(null);
    setStarting(true);
    try {
      const res =
        mode === "basis"
          ? await api.startPreviewImages()
          : mode === "stil"
          ? await api.startStilImages()
          : mode === "anker"
          ? await api.startAnchorImages()
          : await api.startWeltImages();
      stopPolling();
      await poll(res.job_id, setJob);
      pollRef.current = setInterval(() => poll(res.job_id, setJob), 2000);
    } catch (e) {
      setError(
        e?.response?.data?.detail || "Generierung konnte nicht gestartet werden."
      );
    } finally {
      setStarting(false);
    }
  };

  const running = job?.status === "running";
  const agg = job?.aggregate || {};

  const Tab = ({ id, label }) => (
    <button
      data-testid={`preview-mode-${id}`}
      onClick={() => {
        if (!running) setMode(id);
      }}
      className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
        mode === id
          ? "bg-slate-900 text-white"
          : "bg-white text-slate-500 hover:bg-slate-100"
      } ${running ? "cursor-not-allowed opacity-60" : ""}`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="preview-images-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <Link
              to="/unternehmer"
              data-testid="preview-images-back"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-colors hover:bg-slate-100"
            >
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <div>
              <h1 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                <ImageIcon className="h-5 w-5 text-slate-400" />
                KI-Lehrbilder · Prototyp
              </h1>
              <p className="text-xs text-slate-500">
                Isolierter Machbarkeitstest · Winkelschleifer-Welt · nicht produktiv
              </p>
            </div>
          </div>
          <Button
            data-testid="preview-images-generate-button"
            onClick={handleStart}
            disabled={starting || running}
          >
            {starting || running ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generiert…
              </>
            ) : mode === "basis" ? (
              "Bilder generieren"
            ) : mode === "stil" ? (
              "Stil-Härtung starten"
            ) : mode === "anker" ? (
              "Stil-Anker-Test starten"
            ) : (
              "Welt-Bilder generieren"
            )}
          </Button>
        </div>
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-2 px-6 pb-4">
          <Tab id="basis" label="Basis · IMPLEMENT-11" />
          <Tab id="stil" label="Stil-Härtung · IMPLEMENT-12" />
          <Tab id="anker" label="Stil-Anker · IMPLEMENT-13" />
          <Tab id="welt" label="Welt-Kacheln · IMPLEMENT-14" />
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        {error && (
          <div
            data-testid="preview-images-error"
            className="mb-6 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"
          >
            {error}
          </div>
        )}

        {job && (
          <>
            <div className="mb-6 flex flex-wrap items-center gap-3 text-sm text-slate-600">
              <span
                data-testid="preview-images-status"
                className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${
                  running
                    ? "bg-blue-100 text-blue-700"
                    : job.status === "done"
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-rose-100 text-rose-700"
                }`}
              >
                {running ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : job.status === "done" ? (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                ) : (
                  <AlertTriangle className="h-3.5 w-3.5" />
                )}
                {running
                  ? `Generierung läuft … ${job.done}/${job.total}`
                  : job.status === "done"
                  ? "Abgeschlossen"
                  : "Fehler"}
              </span>
              {job.world_title && (
                <span className="text-slate-400">Welt: {job.world_title}</span>
              )}
              {job.error && <span className="text-rose-600">{job.error}</span>}
            </div>

            <div
              data-testid="preview-images-aggregate"
              className="mb-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6"
            >
              <StatPill
                label="Bilder"
                value={`${agg.count_success ?? 0}/${agg.count_total ?? job.total}`}
                testid="agg-count"
              />
              <StatPill
                label="Gesamtdauer"
                value={job.total_seconds ? `${job.total_seconds} s` : "läuft…"}
                testid="agg-total"
              />
              <StatPill label="Ø pro Bild" value={fmtSec(agg.avg_total_seconds)} testid="agg-avg" />
              <StatPill label="Schnellstes" value={fmtSec(agg.min_total_seconds)} testid="agg-min" />
              <StatPill label="Langsamstes" value={fmtSec(agg.max_total_seconds)} testid="agg-max" />
              <StatPill
                label="Fehlerquote"
                value={`${Math.round((agg.error_rate ?? 0) * 100)} %`}
                testid="agg-error-rate"
              />
            </div>
          </>
        )}

        {!job && !error && (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white py-20 text-center">
            <Gauge className="mb-3 h-10 w-10 text-slate-300" />
            <p className="max-w-md text-sm text-slate-500">
              {mode === "basis"
                ? "Noch kein Lauf. Klicken Sie auf „Bilder generieren“, um aus den D1-Aussagen der Winkelschleifer-Welt didaktische Lehrbilder zu erzeugen (max. 10 Bilder)."
                : mode === "stil"
                ? "Noch kein Stil-Lauf. Klicken Sie auf „Stil-Härtung starten“, um 5 Bilder in der einheitlichen CLartext-Bildsprache zu erzeugen und mit IMPLEMENT-11 zu vergleichen."
                : mode === "anker"
                ? "Noch kein Anker-Lauf. Klicken Sie auf „Stil-Anker-Test starten“, um 3 Bilder mit einem festen Referenzbild aus IMPLEMENT-12 zu erzeugen und „ohne Anker“ vs. „mit Anker“ zu vergleichen."
                : "Noch kein Welt-Lauf. Klicken Sie auf „Welt-Bilder generieren“, um KI-Bilder für die bestehenden Lernkacheln der ScanSnap-iX1600-Welt zu erzeugen (Gefahrenmarkierungen, Richtig/Falsch, Maßnahmen; max. 13 Bilder). Die Bilder erscheinen automatisch in Dokumentationswelt + Mitarbeiterflow."}
            </p>
            <p className="mt-2 flex items-center gap-1 text-xs text-slate-400">
              <Clock className="h-3.5 w-3.5" />
              Pro Bild einige Sekunden – die Bilder erscheinen nach und nach.
            </p>
          </div>
        )}

        {mode === "basis" && job?.images?.length > 0 && (
          <div
            data-testid="preview-images-grid"
            className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
          >
            {job.images.map((rec) => (
              <ImageCard key={rec.index} rec={rec} />
            ))}
          </div>
        )}

        {mode === "welt" && job?.images?.length > 0 && (
          <div
            data-testid="welt-images-grid"
            className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
          >
            {job.images.map((rec) => (
              <ImageCard key={rec.index} rec={rec} />
            ))}
          </div>
        )}

        {mode === "stil" && job?.images?.length > 0 && (
          <div
            data-testid="stil-images-grid"
            className="grid grid-cols-1 gap-5 lg:grid-cols-2"
          >
            {job.images.map((rec) => (
              <StilCard key={rec.index} rec={rec} />
            ))}
          </div>
        )}

        {mode === "anker" && job && (
          <>
            {job.anchor_url && (
              <div
                data-testid="anker-reference"
                className="mb-6 flex items-center gap-4 rounded-2xl border border-indigo-200 bg-indigo-50/60 p-4"
              >
                <img
                  src={`${BACKEND_URL}${job.anchor_url}`}
                  alt="Stil-Anker Referenzbild"
                  className="h-24 w-24 rounded-xl border border-indigo-200 bg-white object-contain"
                />
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-indigo-500">
                    Stil-Anker · Referenzbild (IMPLEMENT-12)
                  </div>
                  <p className="mt-1 text-sm text-slate-600">
                    Dient ausschließlich als Stilvorlage – keine inhaltliche Übernahme.
                  </p>
                  {job.anchor_source && (
                    <p className="mt-0.5 text-xs text-slate-400">
                      Quelle: {job.anchor_source}
                    </p>
                  )}
                </div>
              </div>
            )}
            {job.images?.length > 0 && (
              <div
                data-testid="anker-images-grid"
                className="grid grid-cols-1 gap-5 lg:grid-cols-2"
              >
                {job.images.map((rec) => (
                  <StilCard
                    key={rec.index}
                    rec={rec}
                    oldLabel="Ohne Anker · IMPLEMENT-12"
                    newLabel="Mit Anker · IMPLEMENT-13"
                  />
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
